/**
 * Thema-beheer (issue #95). Aurora levert dark mode via `[data-theme="dark"]`
 * op het root-element + semantic tokens (zie tokens.css); deze store bepaalt
 * wélk thema actief is:
 *
 *  - `mode` is het beleid: `'light'`/`'dark'` = afgedwongen (tenant-setting,
 *    superadmin-beheerd), `'switch'` = de gebruiker kiest (default; ook het
 *    vaste beleid van de superadmin-shell).
 *  - In switch-modus wint een eerdere eigen keuze (localStorage `theme`);
 *    zonder keuze volgen we de systeemvoorkeur — live, via het
 *    `prefers-color-scheme`-change-event, tot de gebruiker zelf kiest.
 *
 * Naast `data-theme` zetten we ook CSS `color-scheme`, zodat native UI
 * (scrollbars, date-pickers, form-controls) meekleurt.
 */
import { create } from 'zustand';

export type ThemeMode = 'light' | 'dark' | 'switch';
export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'theme';

function storedTheme(): Theme | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null; // private browsing — geen persistentie, wel gewoon werken
  }
}

const systemQuery = (): MediaQueryList | null =>
  typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;

function systemTheme(): Theme {
  return systemQuery()?.matches ? 'dark' : 'light';
}

function applyToDocument(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.setAttribute('data-theme', 'dark');
  } else {
    root.removeAttribute('data-theme');
  }
  root.style.colorScheme = theme;
}

interface ThemeState {
  /** Effectief toegepast thema. */
  theme: Theme;
  /** Actief beleid (tenant-setting; superadmin-shell = altijd 'switch'). */
  mode: ThemeMode;
  /** Beleid toepassen (vanuit tenant-meta of shell-bootstrap). */
  setMode: (mode: ThemeMode) => void;
  /** Gebruikerskeuze via de schakelaar (alleen zinvol in switch-modus). */
  toggle: () => void;
}

// Systeem-volg-listener leeft buiten de store-state (geen re-renders nodig).
let unfollowSystem: (() => void) | null = null;

function followSystem(onChange: (theme: Theme) => void): void {
  unfollowSystem?.();
  const query = systemQuery();
  if (!query) return;
  const handler = (e: MediaQueryListEvent): void => onChange(e.matches ? 'dark' : 'light');
  query.addEventListener('change', handler);
  unfollowSystem = () => {
    query.removeEventListener('change', handler);
    unfollowSystem = null;
  };
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: 'light',
  mode: 'switch',

  setMode(mode) {
    let theme: Theme;
    if (mode === 'light' || mode === 'dark') {
      // Afgedwongen beleid → eigen keuze en systeemvoorkeur doen niet mee.
      unfollowSystem?.();
      theme = mode;
    } else {
      const chosen = storedTheme();
      theme = chosen ?? systemTheme();
      if (chosen === null) {
        // Nog geen eigen keuze → live meebewegen met het systeem.
        followSystem((next) => {
          applyToDocument(next);
          set({ theme: next });
        });
      } else {
        unfollowSystem?.();
      }
    }
    applyToDocument(theme);
    set({ mode, theme });
  },

  toggle() {
    if (get().mode !== 'switch') return;
    const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // geen persistentie — keuze geldt dan alleen voor deze sessie
    }
    // Expliciete keuze → stoppen met systeem volgen.
    unfollowSystem?.();
    applyToDocument(next);
    set({ theme: next });
  },
}));
